/**
 * @fileoverview Route error screen for the task surfaces (MOR-2473)
 *
 * The My Tasks and workspace calendar routes stream their events through
 * reactive Convex subscriptions, which surface a failed query by throwing
 * during render. TanStack Router catches that at the route boundary and
 * renders this component in place of the page, so a backend failure reads
 * as an error with a way back instead of a spinner that never ends (the
 * failure mode behind MOR-2473).
 *
 * Reload rather than reset: a Convex query that has errored keeps that
 * result in the client cache until the server re-evaluates it, so remounting
 * the route alone re-throws the same error.
 */

import type { ErrorComponentProps } from "@tanstack/react-router";

import { Button } from "@/ui/primitives/button";
import { extractErrorMessage } from "@/utils/errors";

export function TasksLoadError({ error }: ErrorComponentProps) {
  return (
    <div
      role="alert"
      className="flex h-64 flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <p className="text-sm font-medium">We couldn&apos;t load your tasks.</p>
      <p className="max-w-md text-sm text-muted-foreground">
        {extractErrorMessage(error, "Something went wrong while loading.")}
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => window.location.reload()}
      >
        Reload
      </Button>
    </div>
  );
}
